import React, { useEffect, useState } from 'react';
import {
    Typography, Box, CssBaseline, Tabs, Tab, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Button, TextField, Modal, MenuItem, InputAdornment, IconButton,
    Checkbox, FormControlLabel, Chip, Snackbar, Alert,
} from '@mui/material';
import { EditNote, Key, Cancel, Visibility, VisibilityOff, ContentCopy, Refresh } from '@mui/icons-material';
import axios        from 'axios';
import { global_link } from '../Components/Globallink';
import modalStyle   from '../Components/ModalStyle';
import modalStyleLarge from '../Components/ModalStyleLarge';
import SMS_PROVIDER_PRESETS from '../Components/smsProviderPresets';

// ─── safe empty object so selectedItem.anything never throws ────────────────
const EMPTY = {};

// ─── helpers moved outside component to prevent re-creation and focus loss ─
const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    return String(dateStr).split('T')[0].split(' ')[0];
};

const isExpired = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(formatDateForInput(dateStr));
    if (Number.isNaN(d.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return d < today;
};

const isInactive = (active) => Number(active) !== 1;

/** Generate a strong random password (letters, digits, symbols). */
const generatePassword = (length = 16) => {
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const digits = '23456789';
    const symbols = '!@#$%&*-_=+?';
    const all = upper + lower + digits + symbols;
    const pick = (chars) => chars[Math.floor(Math.random() * chars.length)];
    // Ensure at least one of each category
    const chars = [pick(upper), pick(lower), pick(digits), pick(symbols)];
    for (let i = chars.length; i < length; i++) chars.push(pick(all));
    // Fisher–Yates shuffle
    for (let i = chars.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
};

const SearchBar = ({ searchTerm, setSearchTerm, onClick, btnLabel }) => (
    <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
        <TextField 
            label="Search" 
            variant="outlined" 
            fullWidth 
            value={searchTerm} 
            onChange={(e) => setSearchTerm(e.target.value)} 
            placeholder="Search" 
        />
        <Button variant="contained" fullWidth color="info" sx={{ p: '10px', whiteSpace: 'nowrap' }} onClick={onClick}>
            {btnLabel}
        </Button>
    </Box>
);

const ModalHeader = ({ title, onClose }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        <Cancel onClick={onClose} sx={{ cursor: 'pointer' }} />
    </Box>
);

const PasswordField = ({ showPassword, togglePassword, label, name, value, onChange }) => (
    <TextField
        label={label} 
        name={name} 
        fullWidth 
        variant="outlined" 
        required
        sx={{ mt: '5px' }} 
        value={value !== undefined ? value : undefined}
        onChange={onChange}
        type={showPassword ? 'text' : 'password'}
        InputProps={{ endAdornment: (
            <InputAdornment position="end">
                <IconButton onClick={togglePassword} onMouseDown={(e) => e.preventDefault()} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
            </InputAdornment>
        )}}
    />
);

const OrgSelect = ({ organizations, value, onChange }) => {
    const controlled = value !== undefined;
    return (
        <TextField 
            fullWidth 
            select 
            label="Organization Name" 
            name="organization_id" 
            sx={{ mt: '5px' }}
            required 
            {...(controlled ? { value, onChange } : {})}
        >
            <MenuItem value="" disabled>Select Organization</MenuItem>
            {organizations.map((o, i) => (
                <MenuItem key={i} value={o.organization_id}>{o.organization_name}</MenuItem>
            ))}
        </TextField>
    );
};

// ─── Color Picker component ──────────────────────────────────────────────────
const ColorPickerField = ({ label, name, value, onChange, defaultValue }) => (
    <Box sx={{ mt: '5px' }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}>
            {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
                type="color"
                name={name}
                value={value || defaultValue || '#000000'}
                onChange={onChange}
                style={{
                    width: '50px',
                    height: '40px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    padding: '2px',
                }}
            />
            <TextField
                name={`${name}_text`}
                value={value || defaultValue || '#000000'}
                onChange={onChange}
                size="small"
                sx={{ flex: 1 }}
                inputProps={{ style: { fontFamily: 'monospace' } }}
            />
        </Box>
    </Box>
);

// ─── Webchat active-hours editor ─────────────────────────────────────────────
const WEEKDAYS = [
    { key: 'mon', label: 'Mon' }, { key: 'tue', label: 'Tue' }, { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' }, { key: 'fri', label: 'Fri' }, { key: 'sat', label: 'Sat' },
    { key: 'sun', label: 'Sun' },
];

const DEFAULT_SCHEDULE = {
    mon: { open: '08:00', close: '17:00' },
    tue: { open: '08:00', close: '17:00' },
    wed: { open: '08:00', close: '17:00' },
    thu: { open: '08:00', close: '17:00' },
    fri: { open: '08:00', close: '17:00' },
    sat: null,
    sun: null,
};

const ActiveHoursEditor = ({ enabled, setEnabled, timezone, setTimezone, schedule, setSchedule }) => (
    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', p: 1.5, mt: 1 }}>
        <FormControlLabel
            control={<Checkbox checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Restrict to active hours (unchecked = always live)"
        />
        {enabled && (
            <>
                <TextField
                    label="Timezone" size="small" fullWidth sx={{ mb: 1.5, mt: 0.5 }}
                    value={timezone} onChange={(e) => setTimezone(e.target.value)}
                    helperText='IANA timezone name, e.g. "Africa/Nairobi"'
                />
                {WEEKDAYS.map((d) => {
                    const day = schedule[d.key];
                    const closed = !day;
                    return (
                        <Box key={d.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{ width: 42, fontSize: '0.85rem' }}>{d.label}</Box>
                            <FormControlLabel
                                sx={{ mr: 0 }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!closed}
                                        onChange={(e) =>
                                            setSchedule((prev) => ({
                                                ...prev,
                                                [d.key]: e.target.checked ? { open: '08:00', close: '17:00' } : null,
                                            }))
                                        }
                                    />
                                }
                                label=""
                            />
                            {!closed ? (
                                <>
                                    <TextField
                                        type="time" size="small" value={day.open}
                                        onChange={(e) =>
                                            setSchedule((prev) => ({ ...prev, [d.key]: { ...prev[d.key], open: e.target.value } }))
                                        }
                                    />
                                    <span style={{ fontSize: '0.8rem', color: '#888' }}>to</span>
                                    <TextField
                                        type="time" size="small" value={day.close}
                                        onChange={(e) =>
                                            setSchedule((prev) => ({ ...prev, [d.key]: { ...prev[d.key], close: e.target.value } }))
                                        }
                                    />
                                </>
                            ) : (
                                <Chip label="Closed" size="small" sx={{ fontSize: '0.7rem' }} />
                            )}
                        </Box>
                    );
                })}
            </>
        )}
    </Box>
);

const Settings = () => {
    const [activeTab,    setActiveTab]    = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [newUserPassword, setNewUserPassword] = useState('');
    const [searchTerm,   setSearchTerm]   = useState('');
    const [selectedItem, setSelectedItem] = useState(EMPTY);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    const api       = axios.create({ baseURL: global_link });
    const authToken = process.env.AUTH_TOKEN;

    const getHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem(authToken)}`,
        'Content-Type': 'application/json',
    });

    const notify = (message, severity = 'success') => setNotification({ open: true, message, severity });

    // ── data ──────────────────────────────────────────────────────────────────
    const [organizationData,   setOrganizationData]   = useState([]);
    const [allOrganizations,  setAllOrganizations]   = useState([]); // Fetch all for dropdowns
    const [serverData,         setServerData]         = useState([]);
    const [userData,           setUserData]           = useState([]);
    const [smsData,            setSmsData]            = useState([]);
    const [whatsAppData,       setWhatsAppData]       = useState([]);
    const [twitterData,       setTwitterData]       = useState([]);
    const [facebookData,      setFacebookData]      = useState([]);
    const [instagramData,     setInstagramData]     = useState([]);
    const [webchatData,       setWebchatData]       = useState([]);

    // ── totals ────────────────────────────────────────────────────────────────
    const [totalItems,        setTotalItems]        = useState(0);
    const [totalServerItems,  setTotalServerItems]  = useState(0);
    const [totalUserItems,    setTotalUserItems]    = useState(0);
    const [totalSmsItems,     setTotalSmsItems]     = useState(0);
    const [totalWhatsappItems,setTotalWhatsappItems]= useState(0);
    const [totalTwitterItems,   setTotalTwitterItems]   = useState(0);
    const [totalFacebookItems,  setTotalFacebookItems]  = useState(0);
    const [totalInstagramItems, setTotalInstagramItems] = useState(0);
    const [totalWebchatItems,   setTotalWebchatItems]   = useState(0);

    // ── pagination ───────────────────────────────────────────────────────────
    const [page,        setPage]        = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [serverpage,        setServerPage]        = useState(0);
    const [serverrowsPerPage, setServerRowsPerPage] = useState(10);
    const [userpage,        setUserPage]        = useState(0);
    const [userrowsPerPage, setUserRowsPerPage] = useState(10);
    const [smspage,        setSmsPage]        = useState(0);
    const [smsrowsPerPage, setSmsRowsPerPage] = useState(10);
    const [whatsapppage,        setWhatsAppPage]        = useState(0);
    const [whatsapprowsPerPage, setWhatsAppRowsPerPage] = useState(10);
    const [twitterpage,        setTwitterPage]        = useState(0);
    const [twitterrowsPerPage, setTwitterRowsPerPage] = useState(10);
    const [facebookpage,        setFacebookPage]        = useState(0);
    const [facebookrowsPerPage, setFacebookRowsPerPage] = useState(10);
    const [instagrampage,        setInstagramPage]        = useState(0);
    const [instagramrowsPerPage, setInstagramRowsPerPage] = useState(10);
    const [webchatpage,        setWebchatPage]        = useState(0);
    const [webchatrowsPerPage, setWebchatRowsPerPage] = useState(10);

    // ── modals ────────────────────────────────────────────────────────────────
    const [addOrganizationModal,   setAddOrganizationModal]   = useState(false);
    const [editOrganizationModal,  setEditOrganizationModal]  = useState(false);
    const [addServerModal,         setAddServerModal]         = useState(false);
    const [editServerModal,        setEditServerModal]        = useState(false);
    const [addUserModal,           setAddUserModal]           = useState(false);
    const [editUserModal,          setEditUserModal]          = useState(false);
    const [editUserPasswordModal,  setEditUserPasswordModal]  = useState(false);
    const [addSmsModal,            setAddSmsModal]            = useState(false);
    const [editSmsModal,           setEditSmsModal]           = useState(false);
    const [addWhatsappModal,       setAddWhatsappModal]       = useState(false);
    const [editWhatsappModal,      setEditWhatsappModal]      = useState(false);
    const [addTwitterModal,       setAddTwitterModal]       = useState(false);
    const [editTwitterModal,      setEditTwitterModal]      = useState(false);
    const [addFacebookModal,       setAddFacebookModal]       = useState(false);
    const [editFacebookModal,      setEditFacebookModal]      = useState(false);
    const [addInstagramModal,       setAddInstagramModal]       = useState(false);
    const [editInstagramModal,      setEditInstagramModal]      = useState(false);
    const [addWebchatModal,       setAddWebchatModal]       = useState(false);
    const [editWebchatModal,      setEditWebchatModal]      = useState(false);
    const [embedSnippetModal,     setEmbedSnippetModal]     = useState(false);

    // ── SMS provider template state ──────────────────────────────────────────
    const [smsProviderType, setSmsProviderType] = useState('generic');
    const [smsAccessKey,    setSmsAccessKey]    = useState('');
    const [smsAdvancedOpen, setSmsAdvancedOpen] = useState(false);

    // ── Webchat active-hours + branding state ─────────────────────────────────
    const [webchatHoursEnabled, setWebchatHoursEnabled] = useState(false);
    const [webchatTimezone,     setWebchatTimezone]     = useState('Africa/Nairobi');
    const [webchatSchedule,     setWebchatSchedule]     = useState(DEFAULT_SCHEDULE);

    // ── helpers ───────────────────────────────────────────────────────────────
    const togglePassword = () => setShowPassword((p) => !p);

    // Reset pagination to page 0 whenever search term changes
    useEffect(() => {
        setPage(0);
        setServerPage(0);
        setUserPage(0);
        setSmsPage(0);
        setWhatsAppPage(0);
        setTwitterPage(0);
        setFacebookPage(0);
        setInstagramPage(0);
        setWebchatPage(0);
    }, [searchTerm]);

    // Clear search when switching tabs to prevent cross-tab search leaking
    const handleTabChange = (_, v) => {
        setActiveTab(v);
        setSearchTerm('');
    };

    const handleInputModChange = (e) => {
        const { name, value } = e.target;
        setSelectedItem((prev) => ({ ...prev, [name]: value }));
    };

    const handleColorChange = (e) => {
        const { name, value } = e.target;
        // If it's the text input (ends with _text), extract the base name
        const baseName = name.endsWith('_text') ? name.replace('_text', '') : name;
        setSelectedItem((prev) => ({ ...prev, [baseName]: value }));
    };

    const openEdit = (item, setter) => { setSelectedItem(item); setter(true); };
    const openEditUserPassword = (item) => {
        setSelectedItem(item);
        setNewUserPassword('');
        setShowPassword(false);
        setEditUserPasswordModal(true);
    };
    const openAddModal = (setter) => { setSelectedItem(EMPTY); setter(true); };

    const applySmsPreset = (providerType) => {
        const preset = SMS_PROVIDER_PRESETS[providerType] || SMS_PROVIDER_PRESETS.generic;
        setSmsProviderType(providerType);
        setSelectedItem((prev) => ({
            ...prev,
            provider_type: providerType,
            payload_template: preset.payload_template,
            headers_template: preset.headers_template,
            response_success_path: preset.response_success_path,
            response_success_value: preset.response_success_value,
            response_message_id_path: preset.response_message_id_path
        }));
    };

    const openAddSmsModal = () => {
        setSmsProviderType('generic');
        setSmsAccessKey('');
        setSmsAdvancedOpen(false);
        setSelectedItem(EMPTY);
        setAddSmsModal(true);
    };

    const openEditSms = (item) => {
        let accessKey = '';
        try {
            const extra = item.extra_config
                ? (typeof item.extra_config === 'string' ? JSON.parse(item.extra_config) : item.extra_config)
                : {};
            accessKey = extra.access_key || '';
        } catch (_) { /* ignore */ }

        setSmsProviderType(item.provider_type || 'generic');
        setSmsAccessKey(accessKey);
        setSmsAdvancedOpen(false);
        openEdit(item, setEditSmsModal);
    };

    // ── Webchat helpers ────────────────────────────────────────────────────────
    const openAddWebchatModal = () => {
        setSelectedItem(EMPTY);
        setWebchatHoursEnabled(false);
        setWebchatTimezone('Africa/Nairobi');
        setWebchatSchedule(DEFAULT_SCHEDULE);
        setAddWebchatModal(true);
    };

    const openEditWebchat = (item) => {
        let parsed = null;
        try {
            parsed = item.active_hours
                ? (typeof item.active_hours === 'string' ? JSON.parse(item.active_hours) : item.active_hours)
                : null;
        } catch (_) { /* ignore */ }

        setWebchatHoursEnabled(!!parsed);
        setWebchatTimezone(parsed?.timezone || 'Africa/Nairobi');
        setWebchatSchedule(parsed?.schedule || DEFAULT_SCHEDULE);
        openEdit(item, setEditWebchatModal);
    };

    const buildWebchatActiveHoursJson = () => {
        if (!webchatHoursEnabled) return '';
        return JSON.stringify({ timezone: webchatTimezone, schedule: webchatSchedule });
    };

    const webchatEmbedSnippet = (row) => {
        const origin = row?.embed_origin || window.location.origin;
        return `<script src="${origin}/webChat.js" data-widget-id="${row?.widget_id || ''}"></script>`;
    };

    const copyEmbedSnippet = async (row) => {
        try {
            await navigator.clipboard.writeText(webchatEmbedSnippet(row));
            notify('Embed snippet copied to clipboard');
        } catch (e) {
            notify('Could not copy — select and copy manually', 'error');
        }
    };

    // ── fetch functions ───────────────────────────────────────────────────────
    const fetchOrganizationData = async () => {
        try {
            const res = await api.get('/organization/all', {
                params:  { search: searchTerm, limit: rowsPerPage, offset: page * rowsPerPage },
                headers: getHeaders(),
            });
            setOrganizationData(res.data.data);
            setTotalItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchAllOrganizations = async () => {
        try {
            const res = await api.get('/organization/all', {
                params:  { limit: 1000, offset: 0 }, // Fetch all for dropdowns
                headers: getHeaders(),
            });
            setAllOrganizations(res.data.data || []);
        } catch (err) { console.error(err); }
    };

    const fetchServerData = async () => {
        try {
            const res = await api.get('/server/all', {
                params:  { search: searchTerm, limit: serverrowsPerPage, offset: serverpage * serverrowsPerPage },
                headers: getHeaders(),
            });
            setServerData(res.data.data);
            setTotalServerItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchUserData = async () => {
        try {
            const res = await api.get('/user/getuserssupadmin', {
                params:  { search: searchTerm, limit: userrowsPerPage, offset: userpage * userrowsPerPage },
                headers: getHeaders(),
            });
            setUserData(res.data.data);
            setTotalUserItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchSmsData = async () => {
        try {
            const res = await api.get('sms/getadminsmssettings', {
                params:  { search: searchTerm, limit: smsrowsPerPage, offset: smspage * smsrowsPerPage },
                headers: getHeaders(),
            });
            setSmsData(res.data.data);
            setTotalSmsItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchWhatsAppData = async () => {
        try {
            const res = await api.get('/whatsapp/getconfigsadmin', {
                params:  { search: searchTerm, limit: whatsapprowsPerPage, offset: whatsapppage * whatsapprowsPerPage },
                headers: getHeaders(),
            });
            setWhatsAppData(res.data.data);
            setTotalWhatsappItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchTwitterData = async () => {
        try {
            const res = await api.get('/twitter/getconfigsadmin', {
                params:  { search: searchTerm, limit: twitterrowsPerPage, offset: twitterpage * twitterrowsPerPage },
                headers: getHeaders(),
            });
            setTwitterData(res.data.data);
            setTotalTwitterItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchFacebookData = async () => {
        try {
            const res = await api.get('/facebook/getconfigsadmin', {
                params:  { search: searchTerm, limit: facebookrowsPerPage, offset: facebookpage * facebookrowsPerPage },
                headers: getHeaders(),
            });
            setFacebookData(res.data.data);
            setTotalFacebookItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchInstagramData = async () => {
        try {
            const res = await api.get('/instagram/getconfigsadmin', {
                params:  { search: searchTerm, limit: instagramrowsPerPage, offset: instagrampage * instagramrowsPerPage },
                headers: getHeaders(),
            });
            setInstagramData(res.data.data);
            setTotalInstagramItems(res.data.total);
        } catch (err) { console.error(err); }
    };

    const fetchWebchatData = async () => {
        try {
            const res = await api.get('/webchat/getconfigsadmin', {
                params:  { search: searchTerm, limit: webchatrowsPerPage, offset: webchatpage * webchatrowsPerPage },
                headers: getHeaders(),
            });
            setWebchatData(res.data.data || []);
            setTotalWebchatItems(res.data.total || 0);
        } catch (err) { console.error(err); }
    };

    // ── useEffects ────────────────────────────────────────────────────────────
    useEffect(() => { fetchAllOrganizations(); }, []);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchOrganizationData(); }, [searchTerm, page, rowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchServerData();       }, [searchTerm, serverpage,  serverrowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchUserData();         }, [searchTerm, userpage,    userrowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchSmsData();          }, [searchTerm, smspage,     smsrowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchWhatsAppData();     }, [searchTerm, whatsapppage,whatsapprowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchTwitterData();     }, [searchTerm, twitterpage, twitterrowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchFacebookData();     }, [searchTerm, facebookpage, facebookrowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchInstagramData();    }, [searchTerm, instagrampage, instagramrowsPerPage]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchWebchatData();      }, [searchTerm, webchatpage, webchatrowsPerPage]);

    // ── generic submit builder ────────────────────────────────────────────────
    const submit = (method, url, onSuccess) => (event) => {
        event.preventDefault();
        api[method](url, new FormData(event.target), { headers: getHeaders(), withCredentials: true })
            .then(onSuccess)
            .catch(console.error);
    };

    const del = (url, onSuccess) => {
        api.delete(url, { headers: getHeaders(), withCredentials: true })
            .then(onSuccess)
            .catch(console.error);
    };

    // ── Submits ──────────────────────────────────────────────────────────────
    const handleAddOrganizationSubmit    = submit('post', '/organization/add',  () => { setAddOrganizationModal(false);  fetchOrganizationData(); });
    const handleEditOrganizationSubmit   = submit('put',  '/organization/edit', () => { setEditOrganizationModal(false); fetchOrganizationData(); });
    const handleDeleteOrganization = (id) => del(`/organization/delete/${id}`, () => { setEditOrganizationModal(false); fetchOrganizationData(); });

    const handleAddServerSubmit    = submit('post', '/server/add',  () => { setAddServerModal(false);  fetchServerData(); });
    const handleEditServerSubmit   = submit('put',  '/server/edit', () => { setEditServerModal(false); fetchServerData(); });
    const handleDeleteServer = (id) => del(`/server/delete/${id}`, () => { setEditServerModal(false); fetchServerData(); });

    const handleAddUserSubmit          = submit('post', '/user/addusersuperadmin',    () => { setAddUserModal(false);         fetchUserData(); });
    const handleEditUserSubmit         = submit('put',  '/user/editusersuperadmin',   () => { setEditUserModal(false);        fetchUserData(); });
    const handleEditUserPasswordSubmit = submit('put',  '/user/userpasssettingsupdate', () => { setEditUserPasswordModal(false); fetchUserData(); });
    const handleDeleteUser = (id) => del(`/user/deleteuseradmin/${id}`, () => { setEditUserModal(false); fetchUserData(); });

    const handleDeleteSmsSettings = (id) => del(`/sms/deletesmssettings/${id}`, () => { setEditSmsModal(false); fetchSmsData(); });

    const buildSmsFormData = (event, extraFields = {}) => {
        const formData = new FormData(event.target);
        const preset = SMS_PROVIDER_PRESETS[smsProviderType] || SMS_PROVIDER_PRESETS.generic;

        const rawPayload = formData.get('payload_template_raw');
        const rawHeaders = formData.get('headers_template_raw');

        formData.set('provider_type', smsProviderType);
        formData.set('payload_template', rawPayload || preset.payload_template);
        formData.set('headers_template', rawHeaders || preset.headers_template);
        formData.set('response_success_path', preset.response_success_path);
        formData.set('response_success_value', preset.response_success_value);
        formData.set('response_message_id_path', preset.response_message_id_path);
        formData.set('extra_config', smsAccessKey ? JSON.stringify({ access_key: smsAccessKey }) : '');
        formData.delete('payload_template_raw');
        formData.delete('headers_template_raw');
        formData.delete('provider_type_select');

        Object.entries(extraFields).forEach(([k, v]) => formData.set(k, v));

        return formData;
    };

    const handleAddSmsSubmit = (event) => {
        event.preventDefault();
        const formData = buildSmsFormData(event);
        api.post('/sms/addsettings', formData, { headers: getHeaders(), withCredentials: true })
            .then(() => { setAddSmsModal(false); fetchSmsData(); })
            .catch(console.error);
    };

    const handleEditSmsSubmit = (event) => {
        event.preventDefault();
        const formData = buildSmsFormData(event, { id: selectedItem.id || '' });
        api.put('/sms/editsmssettings', formData, { headers: getHeaders(), withCredentials: true })
            .then(() => { setEditSmsModal(false); fetchSmsData(); })
            .catch(console.error);
    };

    const handleAddWhatsappSubmit    = submit('post', '/whatsapp/addconfigs',  () => { setAddWhatsappModal(false);  fetchWhatsAppData(); });
    const handleEditWhatsappSubmit   = submit('put',  '/whatsapp/editconfigs', () => { setEditWhatsappModal(false); fetchWhatsAppData(); });
    const handleDeleteWhatsappConfigs = (id) => del(`/whatsapp/deleteconfigs/${id}`, () => { setEditWhatsappModal(false); fetchWhatsAppData(); });

    const handleEnableWhatsAppCalling = async (uniqueId, status) => {
        if (!uniqueId) return;
        try {
            await api.post(
                '/whatsapp/calling/enable-admin',
                { unique_id: uniqueId, status },
                { headers: getHeaders(), withCredentials: true }
            );
            notify(status === 'ENABLED' ? 'Calling enabled on Meta for this number' : 'Calling disabled on Meta');
            fetchWhatsAppData();
            setSelectedItem((prev) => ({ ...prev, calling_enabled: status === 'ENABLED' ? 1 : 0 }));
        } catch (e) {
            console.error(e);
            const details = e?.response?.data?.details;
            const msg =
                (typeof details === 'string' ? details : details?.error?.message) ||
                e?.response?.data?.error ||
                'Failed to update calling on Meta';
            notify(typeof msg === 'string' ? msg : JSON.stringify(msg), 'error');
        }
    };

    const handleAddTwitterSubmit    = submit('post', '/twitter/addconfigs',  () => { setAddTwitterModal(false);  fetchTwitterData(); });
    const handleEditTwitterSubmit   = submit('put',  '/twitter/editconfigs', () => { setEditTwitterModal(false); fetchTwitterData(); });
    const handleDeleteTwitterConfigs = (id) => del(`/twitter/deleteconfigs/${id}`, () => { setEditTwitterModal(false); fetchTwitterData(); });

    const handleAddFacebookSubmit    = submit('post', '/facebook/addconfigs',  () => { setAddFacebookModal(false);  fetchFacebookData(); });
    const handleEditFacebookSubmit   = submit('put',  '/facebook/editconfigs', () => { setEditFacebookModal(false); fetchFacebookData(); });
    const handleDeleteFacebookConfigs = (id) => del(`/facebook/deleteconfigs/${id}`, () => { setEditFacebookModal(false); fetchFacebookData(); });

    const handleAddInstagramSubmit    = submit('post', '/instagram/addconfigs',  () => { setAddInstagramModal(false);  fetchInstagramData(); });
    const handleEditInstagramSubmit   = submit('put',  '/instagram/editconfigs', () => { setEditInstagramModal(false); fetchInstagramData(); });
    const handleDeleteInstagramConfigs = (id) => del(`/instagram/deleteconfigs/${id}`, () => { setEditInstagramModal(false); fetchInstagramData(); });

    // ── Webchat submits (JSON, not FormData — active_hours needs to be built client-side) ──
    const handleAddWebchatSubmit = (event) => {
        event.preventDefault();
        const fd = new FormData(event.target);
        const body = {
            organization_id: fd.get('organization_id'),
            widget_name: fd.get('widget_name'),
            allowed_domains: fd.get('allowed_domains') || null,
            chat_title: fd.get('chat_title') || 'Chat with us',
            bubble_color: fd.get('bubble_color') || '#1976d2',
            bg_color: fd.get('bg_color') || '#ffffff',
            text_color: fd.get('text_color') || '#1a1a2e',
            active_hours: buildWebchatActiveHoursJson() || null,
            offline_form_title: fd.get('offline_form_title') || null,
            offline_form_message: fd.get('offline_form_message') || null,
        };
        api.post('/webchat/addconfigs', body, { headers: getHeaders(), withCredentials: true })
            .then(() => { setAddWebchatModal(false); fetchWebchatData(); notify('Widget created'); })
            .catch((e) => { console.error(e); notify('Failed to create widget', 'error'); });
    };

    const handleEditWebchatSubmit = (event) => {
        event.preventDefault();
        const fd = new FormData(event.target);
        const body = {
            id: selectedItem.id,
            organization_id: fd.get('organization_id'),
            widget_name: fd.get('widget_name'),
            allowed_domains: fd.get('allowed_domains') || null,
            chat_title: fd.get('chat_title') || 'Chat with us',
            bubble_color: fd.get('bubble_color') || '#1976d2',
            bg_color: fd.get('bg_color') || '#ffffff',
            text_color: fd.get('text_color') || '#1a1a2e',
            active_hours: buildWebchatActiveHoursJson() || null,
            offline_form_title: fd.get('offline_form_title') || null,
            offline_form_message: fd.get('offline_form_message') || null,
            active: fd.get('active'),
        };
        api.put('/webchat/editconfigs', body, { headers: getHeaders(), withCredentials: true })
            .then(() => { setEditWebchatModal(false); fetchWebchatData(); notify('Widget updated'); })
            .catch((e) => { console.error(e); notify('Failed to update widget', 'error'); });
    };

    const handleDeleteWebchatConfig = (id) =>
        del(`/webchat/deleteconfigs/${id}`, () => { setEditWebchatModal(false); fetchWebchatData(); notify('Widget deleted'); });

    // ══════════════════════════════════════════════════════════════════════════
    return (
        <>
            <CssBaseline />
            <Typography variant="h5" sx={{ mb: 2 }}>Settings</Typography>

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ bgcolor: 'background.paper', border: '0.1px solid rgba(207,211,212,0.2)', borderRadius: '5px' }}>
                        <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                            <Tab label="Organization" />
                            <Tab label="Server" />
                            <Tab label="Users" />
                            <Tab label="SMS" />
                            <Tab label="WhatsApp" />
                            <Tab label="Twitter" />
                            <Tab label="Facebook" />
                            <Tab label="Instagram" />
                            <Tab label="Webchat" />
                        </Tabs>
                        {/* ══ ORGANIZATION ══ */}
                        {activeTab === 0 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={() => openAddModal(setAddOrganizationModal)} btnLabel="Add Organization" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Email</TableCell>
                                                <TableCell>Phone</TableCell><TableCell>Members (Cap)</TableCell>
                                                <TableCell>Current</TableCell><TableCell>Recurrent Cost (KES)</TableCell>
                                                <TableCell>Expiry</TableCell><TableCell>Status</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {organizationData.map((row, i) => {
                                                const inactive = isInactive(row.active);
                                                const expired = isExpired(row.due_date);
                                                return (
                                                <TableRow key={i} sx={inactive ? { bgcolor: 'rgba(211, 47, 47, 0.08)' } : undefined}>
                                                    <TableCell sx={inactive ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                                        {row.organization_name}
                                                    </TableCell>
                                                    <TableCell>{row.organization_email}</TableCell>
                                                    <TableCell>{row.organization_phone}</TableCell>
                                                    <TableCell>{row.organization_members}</TableCell>
                                                    <TableCell>{row.user_count}</TableCell>
                                                    <TableCell>{parseFloat(row.recurrent_cost).toLocaleString('en-US')}</TableCell>
                                                    <TableCell sx={expired ? { color: 'error.main', fontWeight: 600 } : undefined}>
                                                        {row.due_date}
                                                        {expired && (
                                                            <Chip label="Expired" size="small" color="error" sx={{ ml: 1, fontSize: '0.65rem', height: 20 }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {inactive
                                                            ? <Chip label="Inactive" size="small" color="error" />
                                                            : <Chip label="Active" size="small" color="success" />}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="warning" sx={{ p: '1px', m: '5px' }} onClick={() => openEdit(row, setEditOrganizationModal)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalItems} page={page} rowsPerPage={rowsPerPage}
                                    onPageChange={(_, v) => setPage(v)} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add Org Modal */}
                                <Modal open={addOrganizationModal} onClose={() => setAddOrganizationModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New Organization" onClose={() => setAddOrganizationModal(false)} />
                                        <form onSubmit={handleAddOrganizationSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><TextField label="Organization Name"   name="organization_name"  fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Organization Email"  name="organization_email" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Phone Number"        name="organization_phone" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Members"             name="organization_members" fullWidth required type="number" sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Plan" name="organization_plan" required sx={{ mt: '5px' }}>
                                                        <MenuItem value="" disabled>Select Plan</MenuItem>
                                                        <MenuItem value="basic">Basic</MenuItem>
                                                        <MenuItem value="standard">Standard</MenuItem>
                                                        <MenuItem value="enterprise">Enterprise</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Pass Phrase" name="organization_pass_prefix" /></Grid>
                                                <Grid item xs={6}><TextField label="Recurrent Cost" name="recurrent_cost" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField name="due_date" fullWidth required type="date" sx={{ mt: '5px' }}
                                                        InputProps={{ inputProps: { min: new Date().toISOString().split('T')[0] } }} />
                                                </Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add Organization</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit Org Modal */}
                                <Modal open={editOrganizationModal} onClose={() => setEditOrganizationModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit Organization | ${selectedItem.organization_name || ''}`} onClose={() => setEditOrganizationModal(false)} />
                                        <form onSubmit={handleEditOrganizationSubmit}>
                                            <Grid container spacing={1}>
                                                <input type="hidden" name="organization_id" value={selectedItem.organization_id || ''} />
                                                <Grid item xs={6}><TextField label="Organization Name"  name="organization_name"  fullWidth required sx={{ mt: '5px' }} value={selectedItem.organization_name  || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Organization Email" name="organization_email" fullWidth required sx={{ mt: '5px' }} value={selectedItem.organization_email || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Phone Number"       name="organization_phone" fullWidth required sx={{ mt: '5px' }} value={selectedItem.organization_phone || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Members"            name="organization_members" fullWidth required type="number" sx={{ mt: '5px' }} value={selectedItem.organization_members || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Plan" name="organization_plan" required sx={{ mt: '5px' }} value={selectedItem.organization_plan || ''} onChange={handleInputModChange}>
                                                        <MenuItem value="" disabled>Select Plan</MenuItem>
                                                        <MenuItem value="basic">Basic</MenuItem>
                                                        <MenuItem value="standard">Standard</MenuItem>
                                                        <MenuItem value="enterprise">Enterprise</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Pass Phrase" name="organization_pass_prefix" value={selectedItem.organization_pass_prefix || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Recurrent Cost" name="recurrent_cost" fullWidth required sx={{ mt: '5px' }} value={selectedItem.recurrent_cost || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField name="due_date" fullWidth required type="date" sx={{ mt: '5px' }} value={formatDateForInput(selectedItem.due_date)} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={12}>
                                                    <TextField fullWidth select label="Status" name="active" required sx={{ mt: '5px' }} value={String(selectedItem.active ?? '')} onChange={handleInputModChange}>
                                                        <MenuItem value="" disabled>Select Status</MenuItem>
                                                        <MenuItem value="1">Active</MenuItem>
                                                        <MenuItem value="0">Inactive</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit Organization</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteOrganization(selectedItem.organization_id)}>Delete Organization</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ SERVER ══ */}
                        {activeTab === 1 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={() => openAddModal(setAddServerModal)} btnLabel="Add Server" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Name</TableCell>
                                                <TableCell>URL / IP</TableCell><TableCell>Port</TableCell>
                                                <TableCell>Status</TableCell><TableCell>Voice Server</TableCell>
                                                <TableCell>Date</TableCell><TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {serverData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.server_name}</TableCell>
                                                    <TableCell>{row.server_url}</TableCell>
                                                    <TableCell>{row.server_port}</TableCell>
                                                    <TableCell>{row.server_active === 1 ? 'Active' : 'Inactive'}</TableCell>
                                                    <TableCell>{row.server_voice_url}</TableCell>
                                                    <TableCell>{row.server_date}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="warning" sx={{ p: '1px', m: '5px' }} onClick={() => openEdit(row, setEditServerModal)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalServerItems} page={serverpage} rowsPerPage={serverrowsPerPage}
                                    onPageChange={(_, v) => setServerPage(v)} onRowsPerPageChange={(e) => { setServerRowsPerPage(parseInt(e.target.value, 10)); setServerPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add Server */}
                                <Modal open={addServerModal} onClose={() => setAddServerModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New Server" onClose={() => setAddServerModal(false)} />
                                        <form onSubmit={handleAddServerSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}><TextField label="Server Name"      name="server_name"      fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Server URL"       name="server_url"       fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Server Port"      name="server_port"      fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Status" name="server_active" required sx={{ mt: '5px' }}>
                                                        <MenuItem value="1">Active</MenuItem>
                                                        <MenuItem value="0">Inactive</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><TextField label="Voice URL" name="server_voice_url" fullWidth sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add Server</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit Server */}
                                <Modal open={editServerModal} onClose={() => setEditServerModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit Server | ${selectedItem.server_name || ''}`} onClose={() => setEditServerModal(false)} />
                                        <form onSubmit={handleEditServerSubmit}>
                                            <Grid container spacing={1}>
                                                <input type="hidden" name="server_id" value={selectedItem.server_id || ''} />
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Server Name"  name="server_name"  fullWidth required sx={{ mt: '5px' }} value={selectedItem.server_name  || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Server URL"   name="server_url"   fullWidth required sx={{ mt: '5px' }} value={selectedItem.server_url   || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Server Port"  name="server_port"  fullWidth required sx={{ mt: '5px' }} value={selectedItem.server_port  || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Status" name="server_active" required sx={{ mt: '5px' }} value={String(selectedItem.server_active ?? '')} onChange={handleInputModChange}>
                                                        <MenuItem value="1">Active</MenuItem>
                                                        <MenuItem value="0">Inactive</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><TextField label="Voice URL" name="server_voice_url" fullWidth sx={{ mt: '5px' }} value={selectedItem.server_voice_url || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit Server</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteServer(selectedItem.server_id)}>Delete Server</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ USERS ══ */}
                        {activeTab === 2 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={() => openAddModal(setAddUserModal)} btnLabel="Add User" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Name</TableCell>
                                                <TableCell>Extension</TableCell><TableCell>Email</TableCell>
                                                <TableCell>Role</TableCell><TableCell>Reg Date</TableCell><TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {userData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.user_name}</TableCell>
                                                    <TableCell>{row.user_extension}</TableCell>
                                                    <TableCell>{row.user_email}</TableCell>
                                                    <TableCell>{row.user_level}</TableCell>
                                                    <TableCell>{row.user_reg_date}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="info"    sx={{ p: '1px', m: '5px' }} onClick={() => openEdit(row, setEditUserModal)}><EditNote sx={{ fontSize: '1.5rem', m: '5px' }} /></Button>
                                                        <Button variant="contained" color="warning" sx={{ p: '1px', m: '5px' }} onClick={() => openEditUserPassword(row)}><Key sx={{ fontSize: '1.5rem', m: '5px' }} /></Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalUserItems} page={userpage} rowsPerPage={userrowsPerPage}
                                    onPageChange={(_, v) => setUserPage(v)} onRowsPerPageChange={(e) => { setUserRowsPerPage(parseInt(e.target.value, 10)); setUserPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add User */}
                                <Modal open={addUserModal} onClose={() => setAddUserModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New User" onClose={() => setAddUserModal(false)} />
                                        <form onSubmit={handleAddUserSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}><TextField label="User Name"  name="user_name"  fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="User Email" name="user_email" fullWidth required type="email" sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="User Phone" name="user_phone" fullWidth required type="tel"   sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="User Level" name="user_level" required sx={{ mt: '5px' }}>
                                                        <MenuItem value="" disabled>Select Level</MenuItem>
                                                        <MenuItem value="admin">Admin</MenuItem>
                                                        <MenuItem value="agent">Agent</MenuItem>
                                                        <MenuItem value="supervisor">Supervisor</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Password" name="user_pass" /></Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add User</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit User */}
                                <Modal open={editUserModal} onClose={() => setEditUserModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit User | ${selectedItem.user_name || ''}`} onClose={() => setEditUserModal(false)} />
                                        <form onSubmit={handleEditUserSubmit}>
                                            <Grid container spacing={1}>
                                                <input type="hidden" name="user_id" value={selectedItem.user_id || ''} />
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="User Name"      name="user_name"      fullWidth required sx={{ mt: '5px' }} value={selectedItem.user_name      || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="User Extension" name="user_extension" fullWidth required sx={{ mt: '5px' }} value={selectedItem.user_extension || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="User Email"     name="user_email"     fullWidth required type="email" sx={{ mt: '5px' }} value={selectedItem.user_email     || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="User Phone"     name="user_phone"     fullWidth required type="tel"   sx={{ mt: '5px' }} value={selectedItem.user_phone     || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="User Level" name="user_level" required sx={{ mt: '5px' }} value={selectedItem.user_level || ''} onChange={handleInputModChange}>
                                                        <MenuItem value="" disabled>Select Level</MenuItem>
                                                        <MenuItem value="admin">Admin</MenuItem>
                                                        <MenuItem value="agent">Agent</MenuItem>
                                                        <MenuItem value="supervisor">Supervisor</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit User</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteUser(selectedItem.user_id)}>Delete User</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Change Password */}
                                <Modal open={editUserPasswordModal} onClose={() => { setEditUserPasswordModal(false); setNewUserPassword(''); }}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Update Password | ${selectedItem.user_name || ''}`} onClose={() => { setEditUserPasswordModal(false); setNewUserPassword(''); }} />
                                        <form onSubmit={(e) => { handleEditUserPasswordSubmit(e); setNewUserPassword(''); }}>
                                            <input type="hidden" name="user_id" value={selectedItem.user_id || ''} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={12}>
                                                    <PasswordField
                                                        showPassword={showPassword}
                                                        togglePassword={togglePassword}
                                                        label="New Password"
                                                        name="user_pass"
                                                        value={newUserPassword}
                                                        onChange={(e) => setNewUserPassword(e.target.value)}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                                        <Button
                                                            type="button"
                                                            variant="outlined"
                                                            color="secondary"
                                                            startIcon={<Refresh />}
                                                            onClick={() => {
                                                                const pwd = generatePassword(16);
                                                                setNewUserPassword(pwd);
                                                                setShowPassword(true);
                                                            }}
                                                            sx={{ flex: 1 }}
                                                        >
                                                            Generate Password
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            variant="outlined"
                                                            color="info"
                                                            startIcon={<ContentCopy />}
                                                            disabled={!newUserPassword}
                                                            onClick={async () => {
                                                                try {
                                                                    await navigator.clipboard.writeText(newUserPassword);
                                                                    notify('Password copied to clipboard');
                                                                } catch (_) {
                                                                    notify('Could not copy password', 'error');
                                                                }
                                                            }}
                                                            sx={{ flex: 1 }}
                                                        >
                                                            Copy
                                                        </Button>
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="warning" fullWidth sx={{ p: '10px', mt: '10px' }}>Update Password</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ SMS ══ */}
                        {activeTab === 3 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={openAddSmsModal} btnLabel="Add SMS Settings" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell>
                                                <TableCell>Provider</TableCell>
                                                <TableCell>Type</TableCell>
                                                <TableCell>Endpoint</TableCell>
                                                <TableCell>Partner ID</TableCell>
                                                <TableCell>API Key</TableCell>
                                                <TableCell>Short Code</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {smsData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.provider}</TableCell>
                                                    <TableCell>
                                                        {SMS_PROVIDER_PRESETS[row.provider_type]?.label || row.provider_type || 'generic'}
                                                    </TableCell>
                                                    <TableCell>{row.end_point}</TableCell>
                                                    <TableCell>{row.partner_id}</TableCell>
                                                    <TableCell>{row.api_key ? `${row.api_key.substring(0, 4)}…${row.api_key.slice(-4)}` : 'N/A'}</TableCell>
                                                    <TableCell>{row.short_code}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="info" sx={{ p: '1px', m: '5px' }} onClick={() => openEditSms(row)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalSmsItems} page={smspage} rowsPerPage={smsrowsPerPage}
                                    onPageChange={(_, v) => setSmsPage(v)} onRowsPerPageChange={(e) => { setSmsRowsPerPage(parseInt(e.target.value, 10)); setSmsPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add SMS */}
                                <Modal open={addSmsModal} onClose={() => setAddSmsModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New SMS Configuration" onClose={() => setAddSmsModal(false)} />
                                        <form onSubmit={handleAddSmsSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth select label="Provider Type" name="provider_type_select" required
                                                        sx={{ mt: '5px' }} value={smsProviderType}
                                                        onChange={(e) => applySmsPreset(e.target.value)}
                                                    >
                                                        {Object.entries(SMS_PROVIDER_PRESETS).map(([key, p]) => (
                                                            <MenuItem key={key} value={key}>{p.label}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><TextField label="Provider Name (display)" name="provider" fullWidth required sx={{ mt: '5px' }} placeholder="e.g. Onfon Media" /></Grid>
                                                <Grid item xs={6}><TextField label="End Point" name="end_point" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="API Key" name="api_key" /></Grid>
                                                <Grid item xs={6}><TextField label="Partner ID / Client ID" name="partner_id" fullWidth sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={12}><TextField label="Short Code / Sender ID" name="short_code" fullWidth required sx={{ mt: '5px' }} /></Grid>

                                                {SMS_PROVIDER_PRESETS[smsProviderType]?.needs_access_key && (
                                                    <Grid item xs={6}>
                                                        <PasswordField
                                                            showPassword={showPassword} togglePassword={togglePassword}
                                                            label="Access Key (optional)" name="access_key_display"
                                                            value={smsAccessKey} onChange={(e) => setSmsAccessKey(e.target.value)}
                                                        />
                                                    </Grid>
                                                )}

                                                <Grid item xs={12}>
                                                    <Button size="small" onClick={() => setSmsAdvancedOpen((v) => !v)} sx={{ mt: 1 }}>
                                                        {smsAdvancedOpen ? 'Hide Advanced (Raw JSON)' : 'Show Advanced (Raw JSON)'}
                                                    </Button>
                                                </Grid>

                                                {smsAdvancedOpen && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Payload Template (JSON)" name="payload_template_raw" fullWidth multiline rows={4}
                                                                sx={{ mt: '5px' }} defaultValue={SMS_PROVIDER_PRESETS[smsProviderType]?.payload_template}
                                                                helperText="Use {{api_key}}, {{partner_id}}, {{short_code}}, {{to}}, {{text}}, {{access_key}} as placeholders."
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Headers Template (JSON)" name="headers_template_raw" fullWidth multiline rows={2}
                                                                sx={{ mt: '5px' }} defaultValue={SMS_PROVIDER_PRESETS[smsProviderType]?.headers_template}
                                                            />
                                                        </Grid>
                                                    </>
                                                )}

                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add SMS Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit SMS */}
                                <Modal open={editSmsModal} onClose={() => setEditSmsModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit SMS | ${selectedItem.organization_name || ''}`} onClose={() => setEditSmsModal(false)} />
                                        <form onSubmit={handleEditSmsSubmit}>
                                            <input type="hidden" name="id" value={selectedItem.id || ''} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField
                                                        fullWidth select label="Provider Type" name="provider_type_select" required
                                                        sx={{ mt: '5px' }} value={smsProviderType}
                                                        onChange={(e) => applySmsPreset(e.target.value)}
                                                    >
                                                        {Object.entries(SMS_PROVIDER_PRESETS).map(([key, p]) => (
                                                            <MenuItem key={key} value={key}>{p.label}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><TextField label="Provider Name (display)" name="provider" fullWidth required sx={{ mt: '5px' }} value={selectedItem.provider || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="End Point" name="end_point" fullWidth required sx={{ mt: '5px' }} value={selectedItem.end_point || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="API Key" name="api_key" value={selectedItem.api_key || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Partner ID / Client ID" name="partner_id" fullWidth sx={{ mt: '5px' }} value={selectedItem.partner_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Short Code / Sender ID" name="short_code" fullWidth required sx={{ mt: '5px' }} value={selectedItem.short_code || ''} onChange={handleInputModChange} /></Grid>

                                                {SMS_PROVIDER_PRESETS[smsProviderType]?.needs_access_key && (
                                                    <Grid item xs={6}>
                                                        <PasswordField
                                                            showPassword={showPassword} togglePassword={togglePassword}
                                                            label="Access Key (optional)" name="access_key_display"
                                                            value={smsAccessKey} onChange={(e) => setSmsAccessKey(e.target.value)}
                                                        />
                                                    </Grid>
                                                )}

                                                <Grid item xs={12}>
                                                    <Button size="small" onClick={() => setSmsAdvancedOpen((v) => !v)} sx={{ mt: 1 }}>
                                                        {smsAdvancedOpen ? 'Hide Advanced (Raw JSON)' : 'Show Advanced (Raw JSON)'}
                                                    </Button>
                                                </Grid>

                                                {smsAdvancedOpen && (
                                                    <>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Payload Template (JSON)" name="payload_template_raw" fullWidth multiline rows={4}
                                                                sx={{ mt: '5px' }} defaultValue={selectedItem.payload_template || SMS_PROVIDER_PRESETS[smsProviderType]?.payload_template}
                                                                helperText="Use {{api_key}}, {{partner_id}}, {{short_code}}, {{to}}, {{text}}, {{access_key}} as placeholders."
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Headers Template (JSON)" name="headers_template_raw" fullWidth multiline rows={2}
                                                                sx={{ mt: '5px' }} defaultValue={selectedItem.headers_template || SMS_PROVIDER_PRESETS[smsProviderType]?.headers_template}
                                                            />
                                                        </Grid>
                                                    </>
                                                )}

                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit SMS</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteSmsSettings(selectedItem.id)}>Delete SMS</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ WHATSAPP ══ */}
                        {activeTab === 4 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={() => openAddModal(setAddWhatsappModal)} btnLabel="Add WhatsApp Settings" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Business Name</TableCell>
                                                <TableCell>Phone ID</TableCell><TableCell>WABA ID</TableCell>
                                                <TableCell>Bearer Token</TableCell><TableCell>Verify Token</TableCell>
                                                <TableCell>Webhook Endpoint</TableCell><TableCell>Calling</TableCell>
                                                <TableCell>Type</TableCell><TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {whatsAppData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.business_name}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.phone_id}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.waba_id || '—'}</TableCell>
                                                    <TableCell>{row.bearer       ? `${row.bearer.substring(0, 4)}…${row.bearer.slice(-4)}`             : 'N/A'}</TableCell>
                                                    <TableCell>{row.verify_token ? `${row.verify_token.substring(0, 4)}…${row.verify_token.slice(-4)}` : 'N/A'}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.end_point || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        {Number(row.calling_enabled) === 1
                                                            ? <Chip label="Enabled" size="small" color="success" />
                                                            : <Chip label="Disabled" size="small" color="default" />}
                                                    </TableCell>
                                                    <TableCell>{row.type}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="info" sx={{ p: '1px', m: '5px' }} onClick={() => openEdit(row, setEditWhatsappModal)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalWhatsappItems} page={whatsapppage} rowsPerPage={whatsapprowsPerPage}
                                    onPageChange={(_, v) => setWhatsAppPage(v)} onRowsPerPageChange={(e) => { setWhatsAppRowsPerPage(parseInt(e.target.value, 10)); setWhatsAppPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add WhatsApp */}
                                <Modal open={addWhatsappModal} onClose={() => setAddWhatsappModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New WhatsApp Configuration" onClose={() => setAddWhatsappModal(false)} />
                                        <form onSubmit={handleAddWhatsappSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}><TextField label="Business Name" name="business_name" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Phone Number ID" name="phone_id" fullWidth required sx={{ mt: '5px' }} helperText="Meta Cloud API phone_number_id" /></Grid>
                                                <Grid item xs={6}><TextField label="WABA ID" name="waba_id" fullWidth sx={{ mt: '5px' }} helperText="WhatsApp Business Account ID (for templates). Optional — auto-resolved from Meta if empty." /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Calling (local flag)" name="calling_enabled" sx={{ mt: '5px' }} defaultValue="0">
                                                        <MenuItem value="0">Disabled</MenuItem>
                                                        <MenuItem value="1">Enabled (DB only)</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Bearer Token"  name="bearer" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Verify Token"  name="verify_token" /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="remote">Remote</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add WhatsApp Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit WhatsApp */}
                                <Modal open={editWhatsappModal} onClose={() => setEditWhatsappModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit WhatsApp | ${selectedItem.organization_name || ''}`} onClose={() => setEditWhatsappModal(false)} />
                                        <form onSubmit={handleEditWhatsappSubmit}>
                                            <input type="hidden" name="unique_id" value={selectedItem.unique_id || ''} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Business Name" name="business_name" fullWidth required sx={{ mt: '5px' }} value={selectedItem.business_name || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Phone Number ID" name="phone_id" fullWidth required sx={{ mt: '5px' }} value={selectedItem.phone_id || ''} onChange={handleInputModChange} helperText="Meta Cloud API phone_number_id" /></Grid>
                                                <Grid item xs={6}><TextField label="WABA ID" name="waba_id" fullWidth sx={{ mt: '5px' }} value={selectedItem.waba_id || ''} onChange={handleInputModChange} helperText="Required for message templates list" /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Calling (local flag)" name="calling_enabled" sx={{ mt: '5px' }}
                                                        value={String(selectedItem.calling_enabled ?? '0')} onChange={handleInputModChange}>
                                                        <MenuItem value="0">Disabled</MenuItem>
                                                        <MenuItem value="1">Enabled (DB only)</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Bearer Token"  name="bearer"       value={selectedItem.bearer       || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Verify Token"  name="verify_token" value={selectedItem.verify_token || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField label="Webhook Endpoint (read-only)" name="end_point" fullWidth sx={{ mt: '5px' }}
                                                        value={selectedItem.end_point || ''} InputProps={{ readOnly: true }}
                                                        helperText="Auto-generated. Use this URL in Meta's webhook settings." />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }} value={selectedItem.type || ''} onChange={handleInputModChange}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="remote">Remote</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Button type="button" variant="outlined" color="success" fullWidth sx={{ p: '10px', mt: '10px' }}
                                                        onClick={() => handleEnableWhatsAppCalling(selectedItem.unique_id, 'ENABLED')}>
                                                        Enable Calling on Meta (fixes #138000)
                                                    </Button>
                                                    <Button type="button" variant="outlined" color="warning" fullWidth sx={{ p: '10px', mt: '6px' }}
                                                        onClick={() => handleEnableWhatsAppCalling(selectedItem.unique_id, 'DISABLED')}>
                                                        Disable Calling on Meta
                                                    </Button>
                                                </Grid>
                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit WhatsApp</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteWhatsappConfigs(selectedItem.unique_id)}>Delete Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ TWITTER ══ */}
                        {activeTab === 5 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={() => openAddModal(setAddTwitterModal)} btnLabel="Add Twitter Settings" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Username</TableCell>
                                                <TableCell>User ID</TableCell><TableCell>Consumer Key</TableCell>
                                                <TableCell>Webhook Endpoint</TableCell><TableCell>Type</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {twitterData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.twitter_username}</TableCell>
                                                    <TableCell>{row.twitter_user_id}</TableCell>
                                                    <TableCell>{row.consumer_key ? `${row.consumer_key.substring(0, 4)}…${row.consumer_key.slice(-4)}` : 'N/A'}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.end_point || 'N/A'}</TableCell>
                                                    <TableCell>{row.type}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="info" sx={{ p: '1px', m: '5px' }} onClick={() => openEdit(row, setEditTwitterModal)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalTwitterItems} page={twitterpage} rowsPerPage={twitterrowsPerPage}
                                    onPageChange={(_, v) => setTwitterPage(v)} onRowsPerPageChange={(e) => { setTwitterRowsPerPage(parseInt(e.target.value, 10)); setTwitterPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add Twitter */}
                                <Modal open={addTwitterModal} onClose={() => setAddTwitterModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New Twitter Configuration" onClose={() => setAddTwitterModal(false)} />
                                        <form onSubmit={handleAddTwitterSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}><TextField label="Twitter Username" name="twitter_username" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Twitter User ID" name="twitter_user_id" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Consumer Key" name="consumer_key" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Consumer Secret" name="consumer_secret" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Access Token" name="access_token" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Access Token Secret" name="access_token_secret" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Bearer Token (optional)" name="bearer_token" /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="tenant">Tenant</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add Twitter Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit Twitter */}
                                <Modal open={editTwitterModal} onClose={() => setEditTwitterModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit Twitter | ${selectedItem.twitter_username || ''}`} onClose={() => setEditTwitterModal(false)} />
                                        <form onSubmit={handleEditTwitterSubmit}>
                                            <input type="hidden" name="unique_id" value={selectedItem.unique_id || ''} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Twitter Username" name="twitter_username" fullWidth required sx={{ mt: '5px' }} value={selectedItem.twitter_username || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Twitter User ID" name="twitter_user_id" fullWidth required sx={{ mt: '5px' }} value={selectedItem.twitter_user_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Consumer Key" name="consumer_key" value={selectedItem.consumer_key || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Consumer Secret" name="consumer_secret" value={selectedItem.consumer_secret || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Access Token" name="access_token" value={selectedItem.access_token || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Access Token Secret" name="access_token_secret" value={selectedItem.access_token_secret || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Bearer Token (optional)" name="bearer_token" value={selectedItem.bearer_token || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField label="Webhook Endpoint (read-only)" name="end_point" fullWidth sx={{ mt: '5px' }}
                                                        value={selectedItem.end_point || ''} InputProps={{ readOnly: true }}
                                                        helperText="Auto-generated. Use this URL in Twitter's webhook settings." />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }} value={selectedItem.type || ''} onChange={handleInputModChange}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="tenant">Tenant</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit Twitter</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteTwitterConfigs(selectedItem.unique_id)}>Delete Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ FACEBOOK ══ */}
                        {activeTab === 6 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={() => openAddModal(setAddFacebookModal)} btnLabel="Add Facebook Settings" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Page Name</TableCell>
                                                <TableCell>Page ID</TableCell><TableCell>Access Token</TableCell>
                                                <TableCell>Webhook Endpoint</TableCell><TableCell>Type</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {facebookData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.page_name}</TableCell>
                                                    <TableCell>{row.page_id}</TableCell>
                                                    <TableCell>{row.page_access_token ? `${row.page_access_token.substring(0, 4)}…${row.page_access_token.slice(-4)}` : 'N/A'}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.end_point || 'N/A'}</TableCell>
                                                    <TableCell>{row.type}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="info" sx={{ p: '1px', m: '5px' }} onClick={() => openEdit(row, setEditFacebookModal)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalFacebookItems} page={facebookpage} rowsPerPage={facebookrowsPerPage}
                                    onPageChange={(_, v) => setFacebookPage(v)} onRowsPerPageChange={(e) => { setFacebookRowsPerPage(parseInt(e.target.value, 10)); setFacebookPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add Facebook */}
                                <Modal open={addFacebookModal} onClose={() => setAddFacebookModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New Facebook Configuration" onClose={() => setAddFacebookModal(false)} />
                                        <form onSubmit={handleAddFacebookSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}><TextField label="Page Name" name="page_name" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="Page ID"  name="page_id"  fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Page Access Token" name="page_access_token" /></Grid>
                                                <Grid item xs={6}><TextField label="App ID (optional)" name="app_id" fullWidth sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="App Secret (optional)" name="app_secret" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Verify Token" name="verify_token" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Webhook Secret (optional)" name="webhook_secret" /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="tenant">Tenant</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add Facebook Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit Facebook */}
                                <Modal open={editFacebookModal} onClose={() => setEditFacebookModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit Facebook | ${selectedItem.page_name || ''}`} onClose={() => setEditFacebookModal(false)} />
                                        <form onSubmit={handleEditFacebookSubmit}>
                                            <input type="hidden" name="unique_id" value={selectedItem.unique_id || ''} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Page Name" name="page_name" fullWidth required sx={{ mt: '5px' }} value={selectedItem.page_name || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Page ID"  name="page_id"  fullWidth required sx={{ mt: '5px' }} value={selectedItem.page_id  || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Page Access Token" name="page_access_token" value={selectedItem.page_access_token || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="App ID (optional)" name="app_id" fullWidth sx={{ mt: '5px' }} value={selectedItem.app_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="App Secret (optional)" name="app_secret" value={selectedItem.app_secret || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Verify Token" name="verify_token" value={selectedItem.verify_token || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Webhook Secret (optional)" name="webhook_secret" value={selectedItem.webhook_secret || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField label="Webhook Endpoint (read-only)" name="end_point" fullWidth sx={{ mt: '5px' }}
                                                        value={selectedItem.end_point || ''} InputProps={{ readOnly: true }}
                                                        helperText="Auto-generated. Use this URL in Meta's webhook settings." />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }} value={selectedItem.type || ''} onChange={handleInputModChange}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="tenant">Tenant</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit Facebook</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteFacebookConfigs(selectedItem.unique_id)}>Delete Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ INSTAGRAM ══ */}
                        {activeTab === 7 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={() => openAddModal(setAddInstagramModal)} btnLabel="Add Instagram Settings" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Username</TableCell>
                                                <TableCell>IG User ID</TableCell><TableCell>Access Token</TableCell>
                                                <TableCell>Webhook Endpoint</TableCell><TableCell>Type</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {instagramData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.ig_username}</TableCell>
                                                    <TableCell>{row.ig_user_id}</TableCell>
                                                    <TableCell>{row.access_token ? `${row.access_token.substring(0, 4)}…${row.access_token.slice(-4)}` : 'N/A'}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{row.end_point || 'N/A'}</TableCell>
                                                    <TableCell>{row.type}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="info" sx={{ p: '1px', m: '5px' }} onClick={() => openEdit(row, setEditInstagramModal)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalInstagramItems} page={instagrampage} rowsPerPage={instagramrowsPerPage}
                                    onPageChange={(_, v) => setInstagramPage(v)} onRowsPerPageChange={(e) => { setInstagramRowsPerPage(parseInt(e.target.value, 10)); setInstagramPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add Instagram */}
                                <Modal open={addInstagramModal} onClose={() => setAddInstagramModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title="New Instagram Configuration" onClose={() => setAddInstagramModal(false)} />
                                        <form onSubmit={handleAddInstagramSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}><TextField label="IG Username" name="ig_username" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><TextField label="IG User ID" name="ig_user_id" fullWidth required sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Access Token" name="access_token" /></Grid>
                                                <Grid item xs={6}><TextField label="App ID (optional)" name="app_id" fullWidth sx={{ mt: '5px' }} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="App Secret (optional)" name="app_secret" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Verify Token" name="verify_token" /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Webhook Secret (optional)" name="webhook_secret" /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="tenant">Tenant</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add Instagram Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit Instagram */}
                                <Modal open={editInstagramModal} onClose={() => setEditInstagramModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Edit Instagram | ${selectedItem.ig_username || ''}`} onClose={() => setEditInstagramModal(false)} />
                                        <form onSubmit={handleEditInstagramSubmit}>
                                            <input type="hidden" name="unique_id" value={selectedItem.unique_id || ''} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="IG Username" name="ig_username" fullWidth required sx={{ mt: '5px' }} value={selectedItem.ig_username || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="IG User ID" name="ig_user_id" fullWidth required sx={{ mt: '5px' }} value={selectedItem.ig_user_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Access Token" name="access_token" value={selectedItem.access_token || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="App ID (optional)" name="app_id" fullWidth sx={{ mt: '5px' }} value={selectedItem.app_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="App Secret (optional)" name="app_secret" value={selectedItem.app_secret || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Verify Token" name="verify_token" value={selectedItem.verify_token || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><PasswordField showPassword={showPassword} togglePassword={togglePassword} label="Webhook Secret (optional)" name="webhook_secret" value={selectedItem.webhook_secret || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}>
                                                    <TextField label="Webhook Endpoint (read-only)" name="end_point" fullWidth sx={{ mt: '5px' }}
                                                        value={selectedItem.end_point || ''} InputProps={{ readOnly: true }}
                                                        helperText="Auto-generated. Use this URL in Meta's webhook settings." />
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <TextField fullWidth select label="Type" name="type" required sx={{ mt: '5px' }} value={selectedItem.type || ''} onChange={handleInputModChange}>
                                                        <MenuItem value="" disabled>Select Type</MenuItem>
                                                        <MenuItem value="system">System</MenuItem>
                                                        <MenuItem value="tenant">Tenant</MenuItem>
                                                    </TextField>
                                                </Grid>
                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit Instagram</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteInstagramConfigs(selectedItem.unique_id)}>Delete Configuration</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                        {/* ══ WEBCHAT ══ */}
                        {activeTab === 8 && (
                            <Box>
                                <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} onClick={openAddWebchatModal} btnLabel="Add Webchat Widget" />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>Organization</TableCell><TableCell>Widget Name</TableCell>
                                                <TableCell>Widget ID</TableCell><TableCell>Allowed Domains</TableCell>
                                                <TableCell>Active Hours</TableCell><TableCell>Status</TableCell>
                                                <TableCell>Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {webchatData.map((row, i) => (
                                                <TableRow key={i}>
                                                    <TableCell>{row.organization_name}</TableCell>
                                                    <TableCell>{row.widget_name}</TableCell>
                                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{row.widget_id}</TableCell>
                                                    <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {row.allowed_domains || 'Any (unrestricted)'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {row.active_hours ? <Chip label="Restricted" size="small" color="warning" /> : <Chip label="Always live" size="small" color="success" />}
                                                    </TableCell>
                                                    <TableCell>{row.active === 1 ? 'Active' : 'Inactive'}</TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" color="secondary" sx={{ p: '1px', m: '5px' }} onClick={() => { setSelectedItem(row); setEmbedSnippetModal(true); }}>
                                                            <ContentCopy sx={{ fontSize: '1.3rem', m: '5px' }} />
                                                        </Button>
                                                        <Button variant="contained" color="info" sx={{ p: '1px', m: '5px' }} onClick={() => openEditWebchat(row)}>
                                                            <EditNote sx={{ fontSize: '1.5rem', m: '5px' }} />
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination component="div" count={totalWebchatItems} page={webchatpage} rowsPerPage={webchatrowsPerPage}
                                    onPageChange={(_, v) => setWebchatPage(v)} onRowsPerPageChange={(e) => { setWebchatRowsPerPage(parseInt(e.target.value, 10)); setWebchatPage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} />

                                {/* Add Webchat */}
                                <Modal open={addWebchatModal} onClose={() => setAddWebchatModal(false)}>
                                    <Box sx={modalStyleLarge}>
                                        <ModalHeader title="New Webchat Widget" onClose={() => setAddWebchatModal(false)} />
                                        <form onSubmit={handleAddWebchatSubmit}>
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} /></Grid>
                                                <Grid item xs={6}><TextField label="Widget Name" name="widget_name" fullWidth required sx={{ mt: '5px' }} placeholder="e.g. Main Site Chat" /></Grid>
                                                <Grid item xs={12}>
                                                    <TextField label="Allowed Domains (comma-separated, leave blank for unrestricted)" name="allowed_domains" fullWidth sx={{ mt: '5px' }}
                                                        placeholder="https://clientsite.com,https://www.clientsite.com" />
                                                </Grid>
                                                <Grid item xs={12}><TextField label="Chat Title" name="chat_title" fullWidth sx={{ mt: '5px' }} defaultValue="Chat with us" /></Grid>
                                                
                                                {/* Color Pickers */}
                                                <Grid item xs={4}>
                                                    <ColorPickerField 
                                                        label="Bubble Color (HEX)" 
                                                        name="bubble_color" 
                                                        defaultValue="#1976d2"
                                                        onChange={handleColorChange}
                                                        value="#1976d2"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ColorPickerField 
                                                        label="Background Color (HEX)" 
                                                        name="bg_color" 
                                                        defaultValue="#ffffff"
                                                        onChange={handleColorChange}
                                                        value="#ffffff"
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ColorPickerField 
                                                        label="Text Color (HEX)" 
                                                        name="text_color" 
                                                        defaultValue="#1a1a2e"
                                                        onChange={handleColorChange}
                                                        value="#1a1a2e"
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <ActiveHoursEditor
                                                        enabled={webchatHoursEnabled} setEnabled={setWebchatHoursEnabled}
                                                        timezone={webchatTimezone} setTimezone={setWebchatTimezone}
                                                        schedule={webchatSchedule} setSchedule={setWebchatSchedule}
                                                    />
                                                </Grid>

                                                <Grid item xs={6}><TextField label="Offline Form Title" name="offline_form_title" fullWidth sx={{ mt: '5px' }} defaultValue="We're currently offline" /></Grid>
                                                <Grid item xs={6}><TextField label="Offline Form Message" name="offline_form_message" fullWidth sx={{ mt: '5px' }} defaultValue="Leave your details and we'll get back to you." /></Grid>

                                                <Grid item xs={12}><Button type="submit" variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }}>Add Webchat Widget</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Edit Webchat */}
                                <Modal open={editWebchatModal} onClose={() => setEditWebchatModal(false)}>
                                    <Box sx={modalStyleLarge}>
                                        <ModalHeader title={`Edit Webchat | ${selectedItem.widget_name || ''}`} onClose={() => setEditWebchatModal(false)} />
                                        <form onSubmit={handleEditWebchatSubmit}>
                                            <input type="hidden" name="id" value={selectedItem.id || ''} />
                                            <Grid container spacing={1}>
                                                <Grid item xs={6}><OrgSelect organizations={allOrganizations} value={selectedItem.organization_id || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Widget Name" name="widget_name" fullWidth required sx={{ mt: '5px' }} value={selectedItem.widget_name || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={12}>
                                                    <TextField label="Widget ID (read-only)" fullWidth sx={{ mt: '5px' }} value={selectedItem.widget_id || ''} InputProps={{ readOnly: true }} />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField label="Allowed Domains (comma-separated, leave blank for unrestricted)" name="allowed_domains" fullWidth sx={{ mt: '5px' }}
                                                        value={selectedItem.allowed_domains || ''} onChange={handleInputModChange}
                                                        placeholder="https://clientsite.com,https://www.clientsite.com" />
                                                </Grid>
                                                <Grid item xs={12}><TextField label="Chat Title" name="chat_title" fullWidth sx={{ mt: '5px' }} value={selectedItem.chat_title || ''} onChange={handleInputModChange} /></Grid>
                                                
                                                {/* Color Pickers */}
                                                <Grid item xs={4}>
                                                    <ColorPickerField 
                                                        label="Bubble Color (HEX)" 
                                                        name="bubble_color" 
                                                        value={selectedItem.bubble_color || '#1976d2'}
                                                        onChange={handleColorChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ColorPickerField 
                                                        label="Background Color (HEX)" 
                                                        name="bg_color" 
                                                        value={selectedItem.bg_color || '#ffffff'}
                                                        onChange={handleColorChange}
                                                    />
                                                </Grid>
                                                <Grid item xs={4}>
                                                    <ColorPickerField 
                                                        label="Text Color (HEX)" 
                                                        name="text_color" 
                                                        value={selectedItem.text_color || '#1a1a2e'}
                                                        onChange={handleColorChange}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <ActiveHoursEditor
                                                        enabled={webchatHoursEnabled} setEnabled={setWebchatHoursEnabled}
                                                        timezone={webchatTimezone} setTimezone={setWebchatTimezone}
                                                        schedule={webchatSchedule} setSchedule={setWebchatSchedule}
                                                    />
                                                </Grid>

                                                <Grid item xs={6}><TextField label="Offline Form Title" name="offline_form_title" fullWidth sx={{ mt: '5px' }} value={selectedItem.offline_form_title || ''} onChange={handleInputModChange} /></Grid>
                                                <Grid item xs={6}><TextField label="Offline Form Message" name="offline_form_message" fullWidth sx={{ mt: '5px' }} value={selectedItem.offline_form_message || ''} onChange={handleInputModChange} /></Grid>

                                                <Grid item xs={12}>
                                                    <TextField fullWidth select label="Status" name="active" required sx={{ mt: '5px' }} value={String(selectedItem.active ?? '1')} onChange={handleInputModChange}>
                                                        <MenuItem value="1">Active</MenuItem>
                                                        <MenuItem value="0">Inactive</MenuItem>
                                                    </TextField>
                                                </Grid>

                                                <Grid item xs={6}><Button type="submit" variant="contained" color="info"  fullWidth sx={{ p: '10px', mt: '10px' }}>Edit Widget</Button></Grid>
                                                <Grid item xs={6}><Button type="button" variant="contained" color="error" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => handleDeleteWebchatConfig(selectedItem.id)}>Delete Widget</Button></Grid>
                                            </Grid>
                                        </form>
                                    </Box>
                                </Modal>

                                {/* Embed Snippet */}
                                <Modal open={embedSnippetModal} onClose={() => setEmbedSnippetModal(false)}>
                                    <Box sx={modalStyle}>
                                        <ModalHeader title={`Embed Snippet | ${selectedItem.widget_name || ''}`} onClose={() => setEmbedSnippetModal(false)} />
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                                            Paste this immediately before the closing <code>&lt;/body&gt;</code> tag on the client's website.
                                        </Typography>
                                        <TextField
                                            fullWidth multiline rows={3} value={webchatEmbedSnippet(selectedItem)}
                                            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                                        />
                                        <Button variant="contained" color="info" fullWidth sx={{ p: '10px', mt: '10px' }} onClick={() => copyEmbedSnippet(selectedItem)}>
                                            Copy to Clipboard
                                        </Button>
                                    </Box>
                                </Modal>
                            </Box>
                        )}

                    </Box>
                </Grid>
            </Grid>

            <Snackbar open={notification.open} autoHideDuration={4000} onClose={() => setNotification((n) => ({ ...n, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={notification.severity} onClose={() => setNotification((n) => ({ ...n, open: false }))}>{notification.message}</Alert>
            </Snackbar>
        </>
    );
};

export default Settings;